<template>
  <div
    v-if="stepData"
    class="step"
  >
    <component
      :is="question.questionComponent"
      v-for="question in questions"
      :key="question.questionId || question.questionComponent"
      product-name="bike"
      step-id="vehicle"
      :question-id="question.questionId"
      v-bind="question.params || {}"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="stepData"
      :step-shared-data="stepSharedData"
      @toast="showToast($event)"
      @reset-observer="$emit('reset-observer')"
      @input="stepData = { ...stepData, ...$event }"
      @set-step-shared-data="setStepSharedData"
    />
  </div>
</template>
<script>
import { mapActions } from 'vuex';
import { cloneDeep } from 'lodash';

import useExperimentMinimiseHelptext from '@/composables/useExperimentMinimiseHelptext';

export default {
  name: 'form-bike-step-1',
  props: {
    formObserver: {
      type: Object,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      stepData: null,
      stepSharedData: {},
    };
  },
  computed: {
    questions () {
      return [
        {
          questionComponent: 'question-vehicle',
          questionId: 'vehicle',
        },
        {
          questionComponent: 'question-vehicle-alarm',
          questionId: 'vehicle-alarm',
          params: {
            renderAfter: ['vehicleConfirm'],
            title: 'Is the bike fitted with an alarm/immobiliser?',
          },
        },
        {
          questionComponent: 'question-vehicle-tracking-device',
          questionId: 'vehicle-tracking-device',
          params: {
            renderAfter: ['vehicleConfirm'],
          },
        },
        {
          questionComponent: 'question-vehicle-physical-security',
          questionId: 'vehicle-physical-security',
          params: {
            renderAfter: ['vehicleConfirm'],
          },
        },
        {
          questionComponent: 'question-modifications',
          questionId: 'modifications',
          params: {
            renderAfter: ['vehicleConfirm'],
            defaultValues: {
              vehicleModifiedFromManufacturer: false,
            },
            title: 'Has this bike been modified in any way?',
            helpText: {
              value: `
                <p>
                  Modifications are non-standard changes made to the bike after manufacture, such as upgraded alloys or engine/exhaust alterations etc.
                </p>
                <p>
                  You can add as many modifications as you like. Some insurers may offer a discount for particular modifications. You don't need to include any Optional Extras that were added by the manufacturer when the vehicle was built.
                </p>
                <p>
                  If you have not declared a modification and you make a claim, then your claim may be refused and your insurance will be void.
                </p>
              `,
              collapsible: true,
              openerText: this.isExperimentMinimiseHelptext ? 'Understanding Vehicle Modifications' : 'Tell me more about the vehicle modifications',
            },
          },
        },
        {
          questionComponent: 'question-base-radio-boolean',
          questionId: 'vehicle-grey-or-import',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'vehicleGreyOrImport',
            },
            title: 'Is the bike imported?',
            helpText: 'Any bike that was not made specifically to UK specification for sale in this country is considered an import.You can find this information in your vehicle registration document (V5C logbook).',
          },
        },
        {
          questionComponent: 'question-estimated-value',
          questionId: 'estimated-value',
          params: {
            customFields: {
              vehicleLookupPrice: 'vehicleLookupPrice',
              estimatedValue: 'estimatedValue',
            },
            renderAfter: ['vehicleConfirm'],
            title: 'Roughly, how much is the bike worth? (Excluding accessories)',
            helpText: {
              value: '<br/><p>This is an estimated market value for your vehicle, from an independent vehicle valuation provider. It depends on things like mileage, optional extras and overall condition.</p><p>If the vehicle is written off or stolen, the insurer will usually pay out the market value. This value is not necessarily the amount you\'ll receive in the event of a claim.</p><p>If you don\'t agree with the valuation you can still enter what you think your vehicle is worth.</p>',
              collapsible: true,
              openerText: 'How is this value calculated?',
              closerText: 'How is this value calculated?',
            },
            inputPrefix: '£',
            placeholder: 'e.g. 5000',
            maxLength: 8,
            validationRules: {
              estimatedValue: {
                required: true,
                integer: true,
                between: [50, 9999999],
                minValue: 50,
              },
            },
            validationErrors: {
              estimatedValue: {
                required: 'Please enter the estimated value of your bike.',
                integer: 'Please only enter numbers e.g. £5000.',
                between: 'The estimated value of your bike must be between £50 and £99999999.',
                minValue: 'The estimated value of your bike must be greater than £50.',
              },
            },
          },
        },
        {
          questionComponent: 'question-base-number',
          questionId: 'estimated-accesories-value',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'estimatedAccesoriesValue',
            },
            validationRules: {
              field: {
                required: true,
                integer: true,
                between: [0, 99999999],
              },
            },
            validationErrors: {
              field: {
                required: 'Please enter the estimated value of fitted accessories.',
                integer: 'Please only enter numbers e.g. £5000.',
                between: 'The estimated value of fitted accessories must be between £0 and £99999999.',
              },
            },
            title: 'What is the estimated value of fitted accessories?',
            helpText: 'Examples would be luggage, handlebar grips & tank protectors etc. Please do not include vehicle modfiications as there is a separate question for this.',
            inputPrefix: '£',
            placeholder: 'e.g. 200',
            maxLength: 8,
          },
        },
        {
          questionComponent: 'question-vehicle-purchase',
          questionId: 'vehicle-purchase',
          params: {
            renderAfter: ['vehicleConfirm'],
          },
        },
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'vehicle-kept',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'vehicleKept',
            },
            title: 'Where is this bike kept overnight?',
            helpText: 'Please select where it is kept <strong><u>overnight</u></strong> the majority of the time.',
            optionsConfig: {
              otherPlaceholder: 'Please select the location',
              mainItems: ['R', 'D', 'G'],
              options: {
                R: 'On the Road',
                D: 'On a Private Driveway',
                G: 'In a Locked Garage',
                CP: 'Car Park',
                LC: 'Locked Compound',
                PP: 'Kept on Private Property',
                C: 'Carport',
                UC: 'Unlocked Compound',
                SP: 'Secure Resident Parking',
                UP: 'Unsecure Resident Parking',
              },
            },
          },
        },
      ];
    },
  },
  watch: {
    stepData: {
      handler (val) {
        this.$emit('step-data', val);
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions(['showToast']),

    setStepSharedData ({ estimatedValue }) {
      // no need to prefill estimated value if there's no such field yet at all
      if (!Object.prototype.hasOwnProperty.call(this.stepData, 'estimatedValue')) return;

      this.stepSharedData = { ...this.stepSharedData, estimatedValue };
    },
  },
  async created () {
    this.$emit('tracking', 'vehicle');
    this.stepData = cloneDeep(this.form.vehicle);
    this.formObserver.reset();
  },
  setup () {
    // COMPOSABLES
    const { isExperimentMinimiseHelptext } = useExperimentMinimiseHelptext();

    return {
      isExperimentMinimiseHelptext,
    };
  },
};
</script>
